import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import styled, { ThemeProvider } from 'styled-components'
import { useTranslation } from 'react-i18next'

import theme from '@/lib/theme'

import Checkout from './features/Checkout'
import Inactivity from './features/Inactivity'
import Intro from './features/Intro'
import Menu from './features/Menu'
import { useApp } from './lib/hooks'
import Style from './style'
import Loader from './ui/Loader'

const AppWrapper = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  background-color: ${({ theme }) => theme.color.body};
`

const AppVersion = styled.div`
  font-family: ${({ theme }) => theme.font.primary};
  position: absolute;
  bottom: 5px;
  left: 7px;
  z-index: 10000;
  opacity: 0.25;
  letter-spacing: 1px;
  font-size: 12px;
`

function InactivityWatcher({ timeoutMs = 35000, onChange }) {
  const [active, setActive] = useState(true)
  const timerRef = useRef(null)

  useEffect(() => {
    const setActiveAndReset = () => {
      if (!active) {
        setActive(true)
        onChange?.(true)
      }
      resetTimer()
    }

    const handleInactive = () => {
      setActive(false)
      onChange?.(false)
    }

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(handleInactive, timeoutMs)
    }

    // activity events
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach(e => window.addEventListener(e, setActiveAndReset, { passive: true }))

    // initial timer
    resetTimer()

    return () => {
      events.forEach(e => window.removeEventListener(e, setActiveAndReset))
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [timeoutMs, active, onChange])

  return null
}

const Wrapper = ({ children, scheme }) => {
  const currentTheme = theme({ colorScheme: scheme })

  return (
    <ThemeProvider theme={currentTheme}>
      <Style colorScheme={scheme} />
      {children}
    </ThemeProvider>
  )
}

export default function Main() {
  const { isLoading, isError, failureCount, data } = useApp()
  const { i18n } = useTranslation()
  const colorScheme = data?.theme || 'DARK'
  const [active, setActive] = useState(true)
  const location = useLocation()
  const showInactivity = location.pathname && location.pathname !== '/' && !active

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const urlLanguage = urlParams.get('language')

    if (urlLanguage && (urlLanguage === 'pl' || urlLanguage === 'en')) {
      i18n.changeLanguage(urlLanguage)
    } else if (data?.language) {
      const lang = data.language.toLowerCase()
      if (lang === 'pl' || lang === 'en') {
        i18n.changeLanguage(lang)
      }
    }
  }, [data?.language, i18n])

  if (isError || (failureCount > 0 && isLoading)) {
    return (
      <Wrapper scheme={colorScheme}>
        <Loader text="I'm trying to connect with an app - please wait or check your network connection..." />
      </Wrapper>
    )
  }

  if (isLoading)
    return (
      <Wrapper scheme={colorScheme}>
        <Loader />
      </Wrapper>
    )

  return (
    <Wrapper scheme={colorScheme}>
      <InactivityWatcher timeoutMs={60000} onChange={setActive} />
      <AppWrapper>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{ height: '100%' }}
          >
            <Routes location={location}>
              <Route path="/" element={<Intro />} />
              <Route path="/menu" element={<Menu />} />
              <Route path="/checkout" element={<Checkout />} />
            </Routes>
          </motion.div>
        </AnimatePresence>

        <AppVersion>v1.04</AppVersion>
        {showInactivity && <Inactivity />}
      </AppWrapper>
    </Wrapper>
  )
}
